import React, { useEffect, useState } from 'react';
import {
  CloseOutlined,
  SendOutlined,
  ArrowsAltOutlined,
  ShrinkOutlined,
  DownOutlined,
  RedoOutlined,
} from '@ant-design/icons';
import { Button, Dropdown, Input, Menu, MenuProps, Space, Spin, Tooltip, Typography } from 'antd';
import { AiChatHistoryService, AiChatService, LicenceUserService } from '../../api';
import { useDispatch, useSelector } from 'react-redux';
import { StoreStateInterface } from '../../redux';
import {
  AiChatMessageInterface,
  AiChatSettingsUserInterface,
} from '../../interfaces/aiChatSettings/aiChatMessages.interface';
import { UserRoleEnum } from '../../enums';
import { acceptTermsAction } from '../../redux/auth/auth.actions';
import { AiChatHistoryUsageInterface } from '../../interfaces/aiChatSettings';


function getChatWindowSize(isExpanded: boolean) {
  const { innerHeight, innerWidth } = window;
  
  let width: number;
  let height: number;
  
  // Calculate width based on available space
  if (innerWidth < 600) {
    width = innerWidth - 20; // Small screens: minimal margin
  } else if (!isExpanded) {
    width = 400; // Default width for larger screens
  } else {
    width = innerWidth - 256 - 12; // Expanded mode with desktop margins
  }
  
  // Calculate height based on available space
  if (innerHeight < 800) {
    height = innerHeight - 20; // Small screens: minimal margin
  } else if (!isExpanded) {
    height = 500; // Default height for larger screens
  } else {
    height = innerHeight - 50 - 10; // Expanded mode with desktop margins
  }
  
  return { width, height };
}

function canExpandChatWindow() {
  const { innerWidth, innerHeight } = window;
  return innerWidth > 600 && innerHeight > 500;
}

function AiChatComponent() {
  const dispatch = useDispatch();
  const userAuth = useSelector((state: StoreStateInterface) => state.auth);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);

  const [isOpen, setIsOpen] = useState(false);
  const [size, setSize] = useState(getChatWindowSize(false));
  const [isExpanded, setIsExpanded] = useState(false);

  const [aiChatSettings, setAiChatSettings] = useState<AiChatSettingsUserInterface[]>([]);
  const [selectedAiChatSettings, setSelectedAiChatSettings] = useState<AiChatSettingsUserInterface | null>(null);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [usage, setUsage] = useState<AiChatHistoryUsageInterface | null>(null);

  const [messages, setMessages] = useState<AiChatMessageInterface[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const [userInput, setUserInput] = useState('');

  const chatNotReady = !userAuth?.user?.id || !selectedAiChatSettings || !conversationId;

  useEffect(() => {
    if (!userAuth?.user?.id) return;

    setIsTermsAccepted(!!userAuth.user?.aiChatTermsAccepted);

    AiChatService.getSettings().subscribe((settings) => {
      if (!settings || settings.length === 0) return;
      AiChatHistoryService.getUsage(settings[0].id).subscribe((usage) => {
        if (!usage.visible) return;

        setAiChatSettings(settings);
        setSelectedAiChatSettings(settings[0]);

        const userId = userAuth.user?.id;
        const settingsId = settings[0]?.id;
        const conversationId = `${new Date().valueOf()}-${userId}-${settingsId}`;
        setConversationId(conversationId);
      });
    });
  }, [userAuth?.user?.id]);

  useEffect(() => {
    if (!selectedAiChatSettings) return;

    AiChatHistoryService.getUsage(selectedAiChatSettings.id).subscribe((response) => {
      setUsage(response);
    });
  }, [selectedAiChatSettings]);

  function scrroolToId(id: string) {
    setTimeout(() => {
      const target = document.getElementById(id);
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  }

  function focusInput() {
    setTimeout(() => {
      document.getElementById('ai-chat-input')?.focus();
    }, 0);
  }

  function setChatOpen(state: boolean) {
    setIsOpen(state);
    if (state) focusInput();
  }

  function addUsage() {
    if (!usage) return;
    const numUsed = usage.numUsed + 1;
    let usedUp = usage.numAllowed ? numUsed >= usage.numAllowed : false;
    if (userAuth?.user?.roles === UserRoleEnum.ADMIN) usedUp = false;

    setUsage({ ...usage, numUsed, usedUp });
  }

  function convertResponseToHTML(rawText: string) {
    const lines = rawText.split('\n');
    const output = [];
    let inList = false;

    const closeList = () => {
      if (inList) {
        output.push('</ul>');
        inList = false;
      }
    };

    const applyFormatting = (text: string) => {
      return text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>') // bold
        .replace(/\[(.*?)\]\((.*?)\)/g, '<a href="$2" target="_blank">$1</a>') // markdown links
        .replace(/\[doc(\d+)\]/g, ''); // remove links
      // .replace(/\[doc(\d+)\]/g, '<a href="document$1.pdf" target="_blank">[doc$1]</a>'); // links
    };

    for (let line of lines) {
      line = line.trim();

      // Skip empty lines
      if (line === '') {
        closeList();
        continue;
      }

      // Header (### ...)
      if (line.startsWith('###')) {
        closeList();
        const content = line.replace(/^###\s*/, '').trim();
        output.push(`<h3>${applyFormatting(content)}</h3>`);
        continue;
      }

      // List item (- ...)
      if (line.startsWith('- ')) {
        if (!inList) {
          output.push('<ul>');
          inList = true;
        }
        const item = line.replace(/^- /, '').trim();
        output.push(`<li>${applyFormatting(item)}</li>`);
        continue;
      }

      // Paragraph
      closeList();
      output.push(`<p>${applyFormatting(line)}</p>`);
    }

    // Close list if still open
    closeList();

    return output.join('\n');
  }

  // ================================ HANDLE FUNCTIONS ================================

  function handleSendMessage() {
    const newMessage = userInput.trim();

    if (!newMessage || newMessage.length === 0) return;

    setIsLoading(true);

    setUserInput('');

    const allMessages = [...messages, { role: 'user', message: newMessage }];
    setMessages(allMessages);

    scrroolToId(`ai-chat-message-${allMessages.length - 1}`);

    addUsage();

    AiChatService.send({
      message: newMessage,
      aiChatSettingsId: selectedAiChatSettings!.id,
      conversationId: conversationId!,
    }).subscribe((resp) => {
      let { type, response } = resp;
      if (type !== 'RESPONSE') response = 'Ups, nešto je pošlo po zlu. Pokušaj ponovno.';

      setMessages([...allMessages, { role: 'assistant', message: response }]);
      scrroolToId(`ai-chat-message-${allMessages.length}`);
      setIsLoading(false);
      focusInput();
    });
  }

  function clearConversation() {
    setMessages([]);
    const conversationId = `${new Date().valueOf()}-${userAuth.user?.id}-${selectedAiChatSettings?.id}`;
    setConversationId(conversationId);
  }

  function handleResize() {
    const newIsExpanded = !isExpanded;
    setIsExpanded(newIsExpanded);
    setSize(getChatWindowSize(newIsExpanded));
    focusInput();
  }

  function acceptTerms() {
    setIsTermsAccepted(true);
    dispatch(acceptTermsAction());
    AiChatService.acceptTerms().subscribe(() => {
      console.log('Terms accepted');
    });
  }

  // ================================ RENDER FUNCTIONS ================================

  function floatingBtnRender() {
    // return (
    //   <div className="chat-floating-btn" onClick={() => setChatOpen(!isOpen)}>
    //     <img src={require('../../../assets/images/bot.svg')} alt="ai-chat" style={{ color: 'white' }} />
    //   </div>
    // );
    return (
      <div className="chat-floating-btn-img" onClick={() => setChatOpen(!isOpen)}>
        <img src={require('../../../assets/images/biltenko.png')} alt="ai-chat" style={{ color: 'white' }} />
      </div>
    );
  }

  function noMessagesRender() {
    return (
      <div className="chat-window__body-messages-empty">
        <p>Dobro došli! Ja sam Biltenko,</p>
        <p>Vaš AI asistent za javnu nabavu.</p>
        <p>Kako Vam mogu pomoći?</p>
        <p className="chat-window__body-messages-empty-notice">
          Molimo da u razgovor ne unosite osobne podatke
          <br />
          (npr. imena, OIB, kontakte).
        </p>
      </div>
    );
  }

  function messageRender(message: AiChatMessageInterface, key: number) {
    return (
      <div
        id={`ai-chat-message-${key}`}
        key={key}
        className={`chat-window__body-messages-bubble ${
          message.role !== 'user'
            ? 'chat-window__body-messages-bubble--right'
            : 'chat-window__body-messages-bubble--left'
        }`}
      >
        <div dangerouslySetInnerHTML={{ __html: convertResponseToHTML(message.message) }} />
      </div>
    );
  }

  function headerNameRender() {
    const hasMultipleModels = aiChatSettings.length > 1;
    const items: MenuProps['items'] = aiChatSettings.map((item) => ({
      key: item.id,
      label: `${item.modelName} - ${item.modelVersion}`,
    }));

    return (
      <div className="chat-window__header-name">
        <h1>{selectedAiChatSettings?.modelName}</h1>
        {hasMultipleModels && (
          <Dropdown
            overlay={
              <Menu
                items={items}
                selectable={true}
                defaultSelectedKeys={[items?.[0]?.key?.toString() || '']}
                onClick={(e) => {
                  setSelectedAiChatSettings(aiChatSettings.find((item) => item.id === Number(e.key)) || null);
                }}
              />
            }
          >
            <Typography.Link>
              <Space>
                <span>{selectedAiChatSettings?.modelVersion}</span>
                <DownOutlined />
              </Space>
            </Typography.Link>
          </Dropdown>
        )}
      </div>
    );
  }

  function headerNewChatButtonRender() {
    return (
      <div className="chat-window__header-new-chat-button">
        <Tooltip
          title={<span style={{ color: 'rgba(0, 0, 0, 0.65)', opacity: 0.5 }}>Započnite novi razgovor!</span>}
          placement="bottom"
          color="white"
        >
          <RedoOutlined onClick={() => clearConversation()} />
        </Tooltip>
      </div>
    );
  }

  function headerActionRender() {
    const canExpand = canExpandChatWindow();
    return (
      <div className="chat-window__header-actions">
        {canExpand ? (isExpanded ? <ShrinkOutlined onClick={handleResize} /> : <ArrowsAltOutlined onClick={handleResize} />) : null}
        <CloseOutlined onClick={() => setChatOpen(false)} />
      </div>
    );
  }

  function renderInput() {
    if (usage?.usedUp && !isLoading) {
      return <div className="chat-window__body-input-used-up">Potrošili ste sva dostupna pitanja za ovaj mjesec.</div>;
    }

    return (
      <Spin spinning={isLoading}>
        <Input.TextArea
          id="ai-chat-input"
          showCount
          maxLength={selectedAiChatSettings?.characterLimit || 1000}
          placeholder="Imate pitanje?"
          rows={2}
          value={userInput}
          onChange={(e) => setUserInput(e.target.value)}
          onPressEnter={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          disabled={isLoading}
        />
        <Button
          className="chat-window__body-input-button"
          type="link"
          icon={<SendOutlined />}
          size="middle"
          onClick={() => handleSendMessage()}
          disabled={isLoading}
        />
      </Spin>
    );
  }

  function renderUsage() {
    if (!usage) return null;
    return (
      <div className="chat-window__body-usage">
        Preostalo pitanja: {usage.numUsed} / {usage.numAllowed === null ? '∞' : usage.numAllowed}
      </div>
    );
  }

  function renderChatBody() {
    return (
      <div className="chat-window__body">
        <div className="chat-window__body-messages">
          {messages.length === 0 ? noMessagesRender() : messages.map((message, key) => messageRender(message, key))}
        </div>
        <div className="chat-window__body-input">{renderInput()}</div>
        {renderUsage()}
      </div>
    );
  }

  function renderChatTerms() {
    return (
      <div className="chat-window__body-messages-empty chat-window__body-messages-terms" style={{ fontSize: '12px' }}>
        <p>Biltenko – Vaš digitalni savjetnik za javnu nabavu!</p>
        <p>
          Biltenko je digitalni savjetnik temeljen na umjetnoj inteligenciji (OpenAI). Korištenjem podataka iz baze
          znanja BJN RH, Biltenko pomaže u navigaciji kroz sustav javne nabave i pruža odgovore na Vaša pitanja.
        </p>
        <p>
          Biltenko je savjetnik pogonjen umjetnom inteligencijom te njegovi odgovori mogu sadržavati netočnosti.
          Pozivamo Vas da sve važne informacije provjerite izravno u aplikaciji BJN RH korištenjem filtera i tražilice.
          Odgovornost za korištenje Biltenka u potpunosti je na korisniku.
        </p>
        <p>
          Prije početka korištenja Biltenka, molimo da pročitate i prihvatite Uvjete korištenja:{' '}
          <a href="https://biltenjavnenabave.hr/uvjeti-koristenja" target="_blank">
            {' '}
            BJN RH - Uvjeti korištenja
          </a>
        </p>
        <Button type="primary" onClick={() => acceptTerms()}>
          Prihvaćam Uvjete korištenja
        </Button>
      </div>
    );
  }

  function chatWindowRender() {
    return (
      <div className="chat-window" style={{ width: size.width, height: size.height }}>
        <div className="chat-window__header">
          <div className="chat-window__header-left">
            {headerNameRender()}
            {headerNewChatButtonRender()}
          </div>
          {headerActionRender()}
        </div>
        {isTermsAccepted ? renderChatBody() : renderChatTerms()}
      </div>
    );
  }

  if (chatNotReady) return null;

  return (
    <div className={`ai-chat ${isOpen ? 'ai-chat--open' : ''}`}>
      {isOpen ? chatWindowRender() : floatingBtnRender()}
    </div>
  );
}

export default AiChatComponent;
